// Plays the looping conversation in the Parent Messaging hero: messages arrive,
// a reply is typed out and sent, then the thread resets and starts over.
//
// Messages are appended one at a time and never re-rendered. Each bubble runs
// its entrance animation on mount, so rebuilding the whole list on every tick
// would replay all of them at once and the thread would flash rather than grow.
// After each change the thread smooth-scrolls to the foot of the conversation,
// which is the movement that sells it as a live chat.
//
// Honours prefers-reduced-motion by rendering the finished thread once and
// leaving it alone.

const THREAD = [
  { from: "in", text: "Good morning! Ellie is a little sniffly today but happy.", time: "8:04 AM" },
  { from: "out", text: "Thanks for the heads up — we'll keep an eye on her.", time: "8:09 AM" },
  { from: "in", text: "She has her water bottle and an extra change of clothes.", time: "8:11 AM" },
]
const ARRIVES = { from: "in", text: "Ellie ate most of her snack and helped water the garden.", time: "8:12 AM" }
const REPLY = { from: "out", text: "Perfect. I'll send photos after morning circle.", time: "8:12 AM" }
const FOLLOWS = { from: "in", text: "Also — Grandma is picking her up at 4 today.", time: "8:13 AM" }

const NAMES = { in: "Danielle Ramos", out: "Ms. Harper" }
const PLACEHOLDER = "Type a message…"
const TYPE_MS = 32

function messageEl({ from, text, time }) {
  const wrap = document.createElement("div")
  wrap.className = `chat-msg chat-msg--${from}`
  wrap.innerHTML = `
    <span class="chat-msg__who"></span>
    <span class="chat-msg__bubble"></span>
    <span class="chat-msg__time"></span>`
  wrap.querySelector(".chat-msg__who").textContent = NAMES[from]
  wrap.querySelector(".chat-msg__bubble").textContent = text
  wrap.querySelector(".chat-msg__time").textContent = time
  return wrap
}

function typingEl() {
  const el = document.createElement("div")
  el.className = "chat-typing"
  el.innerHTML = "<span></span><span></span><span></span>"
  return el
}

export function setupChatDemo() {
  const thread = document.querySelector("[data-chat-thread]")
  if (!thread) return

  const draft = document.querySelector("[data-chat-draft]")
  const send = document.querySelector("[data-chat-send]")
  const still = window.matchMedia("(prefers-reduced-motion: reduce)")

  const scrollDown = (smooth = true) => {
    thread.scrollTo({
      top: thread.scrollHeight,
      behavior: smooth && !still.matches ? "smooth" : "auto",
    })
  }

  let typing = null
  const stopTyping = () => {
    typing?.remove()
    typing = null
  }
  const startTyping = () => {
    if (!typing) {
      typing = typingEl()
      thread.append(typing)
    }
    scrollDown()
  }
  // The one place a bubble is added, so the one place that animates.
  const push = (message) => {
    stopTyping()
    thread.append(messageEl(message))
    // Let the new node lay out before scrolling past it.
    requestAnimationFrame(() => scrollDown())
  }

  const setDraft = (text) => {
    draft.textContent = text || PLACEHOLDER
    send.toggleAttribute("data-ready", Boolean(text))
  }

  thread.replaceChildren(...THREAD.map(messageEl))
  setDraft("")

  if (still.matches) {
    ;[ARRIVES, REPLY, FOLLOWS].forEach((m) => thread.append(messageEl(m)))
    scrollDown(false)
    return
  }

  let timers = []
  const at = (ms, fn) => timers.push(setTimeout(fn, ms))

  const run = () => {
    timers.forEach(clearTimeout)
    timers = []
    stopTyping()

    // Trim back to the opening three rather than re-rendering them, so the
    // messages already on screen keep their DOM nodes and stay put.
    while (thread.children.length > THREAD.length) thread.lastElementChild.remove()
    scrollDown(false)
    setDraft("")

    at(1400, startTyping)
    at(3000, () => push(ARRIVES))

    // Type the reply out one character at a time, then "send" it.
    for (let i = 1; i <= REPLY.text.length; i++) {
      at(4200 + i * TYPE_MS, () => setDraft(REPLY.text.slice(0, i)))
    }
    const sentAt = 4200 + REPLY.text.length * TYPE_MS + 600
    at(sentAt, () => {
      push(REPLY)
      setDraft("")
    })

    at(sentAt + 1400, startTyping)
    at(sentAt + 3000, () => push(FOLLOWS))
    at(sentAt + 8000, run)
  }

  // Drives the first run as well — the observer fires as soon as the hero is
  // painted, and nothing needs to tick while it is off screen.
  const observer = new IntersectionObserver(([entry]) => {
    if (entry.isIntersecting) {
      run()
    } else {
      timers.forEach(clearTimeout)
      timers = []
    }
  })
  observer.observe(thread)
}
